export type LegalArticle = {
  title: string;
  eyebrow: string;
  intro: string;
  lastUpdated: string;
  bodyHtml: string;
};

const privacyPolicyHtml = String.raw`<h1 class="text-4xl font-bold mb-2 text-gray-700 dark:text-gray-200">Privacy Policy</h1>
    <p class="text-gray-500 mb-8">Last updated: August 26, 2026</p>

    <div class="space-y-8 text-gray-700">
      <!-- Introduction -->
      <section>
        <h2 class="text-2xl font-bold text-gray-800 mb-3">Introduction</h2>
        <p>
          Fire-Calc.com ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains
          how we collect, use, and safeguard information when you visit our website and use our financial calculators.
        </p>
        <p class="mt-3">
          By using Fire-Calc.com, you agree to the collection and use of information in accordance with this policy.
        </p>
      </section>

      <!-- Information We Collect -->
      <section>
        <h2 class="text-2xl font-bold text-gray-800 mb-3">Information We Collect</h2>

        <h3 class="text-lg font-semibold mt-4 mb-2">Calculator Data</h3>
        <p>
          <strong>We do not collect, store, or transmit any financial data you enter into our calculators.</strong>
          All calculations are performed entirely in your web browser (client-side). The numbers you enter for
          income, savings, debts, and other financial information never leave your device and are not sent to
          our servers.
        </p>

        <h3 class="text-lg font-semibold mt-4 mb-2">Automatically Collected Information</h3>
        <p>When you visit our website, certain information may be automatically collected, including:</p>
        <ul class="list-disc ml-6 mt-2 space-y-1">
          <li>Browser type and version</li>
          <li>Operating system</li>
          <li>Pages visited and time spent on pages</li>
          <li>Referring website (how you found us)</li>
          <li>General geographic location (country/region level)</li>
          <li>Device type (desktop, mobile, tablet)</li>
        </ul>
        <p class="mt-3">
          This information is collected through cookies and similar technologies, primarily through third-party
          services described below.
        </p>
      </section>

      <!-- Cookies -->
      <section>
        <h2 class="text-2xl font-bold text-gray-800 mb-3">Cookies and Tracking Technologies</h2>
        <p>
          Cookies are small text files stored on your device when you visit a website. We use cookies for the
          following purposes:
        </p>

        <div class="mt-4 space-y-4">
          <div class="p-4 bg-gray-50 rounded-lg">
            <h4 class="font-semibold">Essential Cookies</h4>
            <p class="text-sm mt-1">
              These are necessary for the website to function properly. They do not collect personal information.
            </p>
          </div>

          <div class="p-4 bg-gray-50 rounded-lg">
            <h4 class="font-semibold">Analytics Cookies</h4>
            <p class="text-sm mt-1">
              These help us understand how visitors interact with our website, allowing us to improve our content
              and user experience. We use Google Analytics 4 for this purpose; the data we see is aggregate usage
              (pages visited, approximate region, device type), not the numbers you enter into any calculator.
            </p>
          </div>

          <div class="p-4 bg-gray-50 rounded-lg">
            <h4 class="font-semibold">Advertising Cookies</h4>
            <p class="text-sm mt-1">
              Third-party vendors, including Google, use cookies to serve ads based on your prior visits to this
              website or other websites. Google's use of advertising cookies (including the DoubleClick cookie)
              enables it and its partners to serve ads based on your visits to this site and other sites on the
              Internet.
            </p>
          </div>
        </div>

        <p class="mt-4">
          You can control cookie settings through your browser. Most browsers allow you to refuse cookies or
          delete existing cookies. However, this may affect the functionality of some features.
        </p>
      </section>

      <!-- Third-Party Services -->
      <section>
        <h2 class="text-2xl font-bold text-gray-800 mb-3">Third-Party Services</h2>

        <h3 class="text-lg font-semibold mt-4 mb-2">Google AdSense</h3>
        <p>
          We use Google AdSense to display advertisements on our website. Google AdSense uses cookies to serve
          ads based on your prior visits to this website and other websites. Google's use of advertising cookies
          enables it and its partners to serve ads based on your browsing patterns.
        </p>
        <p class="mt-2">
          You can opt out of personalized advertising by visiting
          <a href="https://myadcenter.google.com/" class="text-blue-600 hover:underline dark:text-blue-400" target="_blank" rel="noopener noreferrer">Google's My Ad Center</a>.
          You can also opt out of many third-party advertising cookies through the
          <a href="https://optout.aboutads.info/" class="text-blue-600 hover:underline dark:text-blue-400" target="_blank" rel="noopener noreferrer">Digital Advertising Alliance opt-out page (aboutads.info)</a>
          or the
          <a href="https://optout.networkadvertising.org/" class="text-blue-600 hover:underline dark:text-blue-400" target="_blank" rel="noopener noreferrer">Network Advertising Initiative opt-out page</a>.
        </p>
        <p class="mt-2">
          For more information about how Google uses data, please visit
          <a href="https://policies.google.com/technologies/partner-sites" class="text-blue-600 hover:underline dark:text-blue-400" target="_blank" rel="noopener noreferrer">Google's Privacy & Terms</a>.
        </p>

        <h3 class="text-lg font-semibold mt-4 mb-2">Hosting, Fonts, and Delivery</h3>
        <p>
          Fire-Calc.com is hosted and delivered through Cloudflare, which may process visitor IP addresses to provide
          content delivery and security (including the email-address protection used on our contact page). Web fonts
          are loaded from Google Fonts. These services may collect basic usage data according to their own privacy
          policies. We do not load third-party CSS or JavaScript frameworks from other CDNs.
        </p>
      </section>

      <!-- Data Security -->
      <section>
        <h2 class="text-2xl font-bold text-gray-800 mb-3">Data Security</h2>
        <p>
          We take reasonable measures to protect the limited information we collect. However, no method of
          transmission over the Internet or electronic storage is 100% secure.
        </p>
        <p class="mt-3">
          Since we do not collect or store your financial calculator data, there is no risk of that sensitive
          information being compromised on our end.
        </p>
      </section>

      <!-- Children's Privacy -->
      <section>
        <h2 class="text-2xl font-bold text-gray-800 mb-3">Children's Privacy</h2>
        <p>
          Our website is not directed at children under the age of 13. We do not knowingly collect personal
          information from children under 13. If you are a parent or guardian and believe your child has
          provided us with personal information, please contact us so we can take appropriate action.
        </p>
      </section>

      <!-- Your Rights -->
      <section>
        <h2 class="text-2xl font-bold text-gray-800 mb-3">Your Rights</h2>
        <p>Depending on your location, you may have certain rights regarding your personal information, including:</p>
        <ul class="list-disc ml-6 mt-2 space-y-1">
          <li>The right to access information we hold about you</li>
          <li>The right to request correction of inaccurate information</li>
          <li>The right to request deletion of your information</li>
          <li>The right to opt out of certain data collection practices</li>
          <li>The right to withdraw consent where applicable</li>
        </ul>
        <p class="mt-3">
          Since we do not collect personal financial data from our calculators, most of these rights relate
          to data collected through cookies and third-party services, which can be managed through your
          browser settings and the opt-out links provided above.
        </p>

        <h3 class="text-lg font-semibold mt-4 mb-2">European Economic Area and United Kingdom (GDPR)</h3>
        <p>
          If you are in the EEA or the UK, the limited personal data described in this policy (such as IP address
          and usage data collected through cookies) is processed on the basis of our legitimate interest in
          operating, securing, and improving the site, and on the basis of consent where consent is required for
          advertising cookies. You have the right to access, rectify, or erase your personal data, to restrict or
          object to its processing, and to data portability, and you may lodge a complaint with your local
          supervisory authority. We do not require accounts, and the figures you enter into calculators never
          leave your device. To exercise any of these rights, contact us using the details below.
        </p>

        <h3 class="text-lg font-semibold mt-4 mb-2">California Residents (CCPA/CPRA)</h3>
        <p>
          We collect only the categories of personal information described above: identifiers such as IP address,
          and internet activity such as pages visited, collected through cookies. We do not sell personal
          information for money. Cookie-based advertising may constitute "sharing" for cross-context behavioral
          advertising under the CPRA; you can opt out using the advertising opt-out links above and your browser's
          cookie controls. California residents also have the right to know what personal information we collect,
          to request its deletion, and not to be discriminated against for exercising these rights. To make a
          request, contact us using the details below.
        </p>
      </section>

      <!-- Changes to This Policy -->
      <section>
        <h2 class="text-2xl font-bold text-gray-800 mb-3">Changes to This Privacy Policy</h2>
        <p>
          We may update this Privacy Policy from time to time. We will notify you of any changes by posting
          the new Privacy Policy on this page and updating the "Last updated" date at the top.
        </p>
        <p class="mt-3">
          We encourage you to review this Privacy Policy periodically for any changes. Changes are effective
          immediately upon posting.
        </p>
      </section>

      <!-- Contact -->
      <section>
        <h2 class="text-2xl font-bold text-gray-800 mb-3">Contact Us</h2>
        <p>
          If you have any questions about this Privacy Policy or our privacy practices, email us at
          <a href="mailto:support@fire-calc.com" class="text-blue-600 hover:underline dark:text-blue-400">support@fire-calc.com</a>
          or use our <a href="/contact/" class="text-blue-600 hover:underline dark:text-blue-400">contact page</a>.
        </p>
      </section>

      <!-- Summary Box -->
      <section class="mt-8 p-6 bg-blue-50 rounded-lg">
        <h2 class="text-xl font-bold text-blue-800 mb-3">Privacy Summary</h2>
        <ul class="space-y-2 text-blue-900">
          <li><strong>Your calculator data:</strong> Never collected, stored, or transmitted. Stays on your device.</li>
          <li><strong>Cookies:</strong> Used for site functionality and advertising (Google AdSense).</li>
          <li><strong>Third parties:</strong> Google AdSense (advertising), Google Analytics 4 (aggregate usage), Cloudflare (hosting and delivery), Google Fonts.</li>
          <li><strong>Your control:</strong> Manage cookies in your browser; opt out of personalized ads via Google.</li>
        </ul>
      </section>
    </div>`;

const termsOfServiceHtml = String.raw`<h1 class="text-4xl font-bold mb-2 text-gray-700 dark:text-gray-200">Terms of Service</h1>
    <p class="text-sm text-gray-500 mb-6">Last Updated: January 2026</p>

    <p class="text-lg text-gray-600 mb-6 dark:text-gray-400">
      Welcome to Fire-Calc.com. By accessing or using our website and services, you agree to be bound by these Terms of Service. Please read them carefully before using our calculators and educational resources.
    </p>

    <h2 class="text-2xl font-bold text-gray-700 mb-2">1. Acceptance of Terms</h2>
    <p class="text-lg text-gray-600 mb-6 dark:text-gray-400">
      By accessing and using Fire-Calc.com, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service. If you do not agree with any part of these terms, you must not use our website.
    </p>

    <h2 class="text-2xl font-bold text-gray-700 mb-2">2. Educational Purpose Only - Not Financial Advice</h2>
    <div class="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
      <p class="text-lg text-gray-700 font-semibold mb-2">Important Disclaimer:</p>
      <p class="text-gray-700 dark:text-gray-300">
        The calculators, tools, guides, and all content provided on Fire-Calc.com are for <strong>educational and informational purposes only</strong>. They do not constitute financial, investment, tax, or legal advice. The results generated by our calculators are estimates based on the information you provide and general assumptions—they should not be relied upon as precise predictions of future outcomes.
      </p>
    </div>
    <p class="text-lg text-gray-600 mb-6 dark:text-gray-400">
      We strongly recommend that you consult with a qualified financial advisor, tax professional, or other appropriate expert before making any financial decisions. Your individual circumstances, including but not limited to your income, expenses, tax situation, and risk tolerance, may significantly affect the applicability of any information provided on this site.
    </p>

    <h2 class="text-2xl font-bold text-gray-700 mb-2">3. No Guarantees or Warranties</h2>
    <p class="text-lg text-gray-600 mb-6 dark:text-gray-400">
      Fire-Calc.com provides its services "as is" and "as available" without any warranties of any kind, either express or implied. We do not guarantee the accuracy, completeness, reliability, or suitability of any calculations, projections, or information provided. Financial markets, tax laws, and personal circumstances can change, and our tools may not reflect the most current information or your specific situation.
    </p>

    <h2 class="text-2xl font-bold text-gray-700 mb-2">4. Limitation of Liability</h2>
    <p class="text-lg text-gray-600 mb-6 dark:text-gray-400">
      To the fullest extent permitted by applicable law, Fire-Calc.com, its owners, operators, and affiliates shall not be liable for any direct, indirect, incidental, special, consequential, or punitive damages arising out of or related to your use of, or inability to use, our website, calculators, or any content provided. This includes, but is not limited to, any financial losses, investment decisions, or actions taken based on information obtained from this site.
    </p>

    <h2 class="text-2xl font-bold text-gray-700 mb-2">5. User Responsibilities</h2>
    <p class="text-lg text-gray-600 mb-4 dark:text-gray-400">When using Fire-Calc.com, you agree to:</p>
    <ul class="list-disc list-inside text-lg text-gray-600 mb-6 space-y-2">
      <li>Use the site only for lawful purposes and in accordance with these Terms</li>
      <li>Provide accurate information when using our calculators</li>
      <li>Verify any calculations independently before making financial decisions</li>
      <li>Not attempt to interfere with or disrupt the operation of the website</li>
      <li>Not use automated systems or software to extract data from the site without permission</li>
    </ul>

    <h2 class="text-2xl font-bold text-gray-700 mb-2">6. Intellectual Property</h2>
    <p class="text-lg text-gray-600 mb-6 dark:text-gray-400">
      All content on Fire-Calc.com, including but not limited to text, graphics, logos, calculator designs, and software, is the property of Fire-Calc.com and is protected by copyright and other intellectual property laws. You may use our calculators and view content for personal, non-commercial purposes. You may not reproduce, distribute, modify, or create derivative works from our content without prior written permission.
    </p>

    <h2 class="text-2xl font-bold text-gray-700 mb-2">7. Third-Party Links and Services</h2>
    <p class="text-lg text-gray-600 mb-6 dark:text-gray-400">
      Our website may contain links to third-party websites or services that are not owned or controlled by Fire-Calc.com. We have no control over, and assume no responsibility for, the content, privacy policies, or practices of any third-party sites or services. You acknowledge and agree that we shall not be responsible or liable for any damage or loss caused by your use of any such third-party content or services.
    </p>

    <h2 class="text-2xl font-bold text-gray-700 mb-2">8. Advertising</h2>
    <p class="text-lg text-gray-600 mb-6 dark:text-gray-400">
      Fire-Calc.com displays advertisements provided by third-party advertising networks, including Google AdSense. These advertisements help support the free availability of our tools and content. The appearance of advertisements on our site does not constitute an endorsement of the advertised products or services. Please refer to our <a href="/privacy-policy/" class="text-blue-600 hover:underline dark:text-blue-400">Privacy Policy</a> for information about how advertising partners may collect and use data.
    </p>

    <h2 class="text-2xl font-bold text-gray-700 mb-2">9. Modifications to Terms</h2>
    <p class="text-lg text-gray-600 mb-6 dark:text-gray-400">
      We reserve the right to modify these Terms of Service at any time. Changes will be effective immediately upon posting to this page, with the "Last Updated" date revised accordingly. Your continued use of Fire-Calc.com after any changes constitutes your acceptance of the new Terms. We encourage you to review this page periodically.
    </p>

    <h2 class="text-2xl font-bold text-gray-700 mb-2">10. Termination</h2>
    <p class="text-lg text-gray-600 mb-6 dark:text-gray-400">
      We reserve the right to terminate or suspend access to our website at any time, without prior notice or liability, for any reason, including but not limited to a breach of these Terms.
    </p>

    <h2 class="text-2xl font-bold text-gray-700 mb-2">11. Governing Law</h2>
    <p class="text-lg text-gray-600 mb-6 dark:text-gray-400">
      These Terms of Service shall be governed by and construed in accordance with the laws of the United States, without regard to its conflict of law provisions.
    </p>

    <h2 class="text-2xl font-bold text-gray-700 mb-2">12. Contact Us</h2>
    <p class="text-lg text-gray-600">
      If you have any questions about these Terms of Service, please contact us at <a href="mailto:support@fire-calc.com" class="text-blue-600 hover:underline dark:text-blue-400">support@fire-calc.com</a>.
    </p>`;

export const legalArticles: Record<string, LegalArticle> = {
  '/privacy-policy': {
    title: 'Privacy Policy',
    eyebrow: 'Legal',
    intro: 'How Fire-Calc handles data collection, cookies, and third-party services.',
    lastUpdated: 'August 2026',
    bodyHtml: privacyPolicyHtml
  },
  '/terms-of-service': {
    title: 'Terms of Service',
    eyebrow: 'Legal',
    intro: 'Terms and conditions for using Fire-Calc calculators and educational resources.',
    lastUpdated: 'January 2026',
    bodyHtml: termsOfServiceHtml
  }
};
